import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Table as MuiTable,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TablePagination,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import api from "../../utils/api";
import Table from "../../components/Table";
import { IncomeExpenseColumns } from "./components/IncomeExpenseColumns";
import IncomeExpenseModal from "./components/IncomeExpenseModal";
import ExpenseTemplatesManagerDialog from "./components/ExpenseTemplatesManagerDialog";
import GenerateFromTemplateDialog from "./components/GenerateFromTemplateDialog";
import { formatMoney } from "../../utils/helpers";

/** Local calendar first and last day of previous month as YYYY-MM-DD for `<input type="date">`. */
function getLastMonthDateRangeISO() {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    const fmt = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { from: fmt(start), to: fmt(end) };
}

function SummaryCard({ label, value, color = "text.primary" }) {
    return (
        <Card sx={{ minWidth: 220 }}>
            <CardContent>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="h6" sx={{ color, mt: 0.5 }}>{value}</Typography>
            </CardContent>
        </Card>
    );
}

export default function IncomeExpenses() {
    const [pagination, setPagination] = useState({ page: 1, perPage: 40, total: 0, totalPages: 0 });
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({ income_total: 0, expense_total: 0, difference: 0 });
    const [showIncomeModal, setShowIncomeModal] = useState(false);
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [showSalaryModal, setShowSalaryModal] = useState(false);
    const [salaryFrom, setSalaryFrom] = useState("");
    const [salaryTo, setSalaryTo] = useState("");
    const [salaryLoading, setSalaryLoading] = useState(false);
    const [salarySaving, setSalarySaving] = useState(false);
    const [salaryRows, setSalaryRows] = useState([]);
    const [salaryMeta, setSalaryMeta] = useState({ total_rows: 0, resolved_rows: 0, total_amount: 0 });
    const [salaryError, setSalaryError] = useState("");
    const [filters, setFilters] = useState({
        direction: "",
        source: "",
        status: "",
        from: "",
        to: "",
    });
    const [editingEntry, setEditingEntry] = useState(null);
    const [showTemplatesManager, setShowTemplatesManager] = useState(false);
    const [showGenerateFromTemplate, setShowGenerateFromTemplate] = useState(false);

    const columns = useMemo(() => IncomeExpenseColumns(), []);

    const fetchRows = useCallback(async (nextFilters = filters, nextPage = pagination.page) => {
        setLoading(true);
        try {
            const [rowsRes, summaryRes] = await Promise.all([
                api.get("admin/income-expenses", {
                    params: {
                        page: nextPage,
                        perPage: pagination.perPage,
                        filter: nextFilters,
                    },
                }),
                api.get("admin/income-expenses/summary", {
                    params: {
                        filter: nextFilters,
                    },
                }),
            ]);

            setRows(rowsRes.data?.data || []);
            const p = rowsRes.data?.meta?.pagination || {};
            setPagination((prev) => ({
                ...prev,
                page: p.page || nextPage,
                total: p.total || 0,
                totalPages: p.totalPages || 0,
            }));
            setSummary(summaryRes.data?.data || { income_total: 0, expense_total: 0, difference: 0 });
        } catch (err) {
            console.error("Failed to load income/expense entries", err);
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.page, pagination.perPage]);

    useEffect(() => {
        fetchRows(filters, 1);
    }, [filters, fetchRows]);

    const exportToExcel = () => {
        const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
        const url = new URL(`${base}/admin/income-expenses/export`, window.location.origin);
        Object.entries(filters || {}).forEach(([key, value]) => {
            if (value !== "" && value != null) {
                url.searchParams.set(`filter[${key}]`, String(value));
            }
        });
        const token =
            new URLSearchParams(window.location.search).get("token") ||
            localStorage.getItem("sessionID");
        if (token) url.searchParams.set("token", token);
        window.open(url.toString(), "_blank");
    };

    const onDelete = async (row) => {
        if (!row?.id) return;
        if (!window.confirm("Delete this entry?")) return;
        try {
            await api.delete(`admin/income-expenses/${row.id}`);
            fetchRows(filters, pagination.page);
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                (typeof err.response?.data === "string" ? err.response.data : null) ||
                "Could not delete entry.";
            window.alert(msg);
        }
    };

    const loadSalaryPreview = async () => {
        if (!salaryFrom || !salaryTo) return;
        setSalaryLoading(true);
        setSalaryError("");
        try {
            const res = await api.post("admin/income-expenses/salary/preview", {
                from: salaryFrom,
                to: salaryTo,
            });
            const payload = res.data?.data || {};
            setSalaryRows(payload.rows || []);
            setSalaryMeta({
                total_rows: Number(payload.total_rows || 0),
                resolved_rows: Number(payload.resolved_rows || 0),
                total_amount: Number(payload.total_amount || 0),
            });
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                (typeof err.response?.data === "string" ? err.response.data : null) ||
                "Failed to load salary preview.";
            setSalaryError(msg);
            setSalaryRows([]);
            setSalaryMeta({ total_rows: 0, resolved_rows: 0, total_amount: 0 });
        } finally {
            setSalaryLoading(false);
        }
    };

    const removeSalaryRow = (lessonId) => {
        setSalaryRows((prev) => prev.filter((row) => Number(row.lesson_id) !== Number(lessonId)));
    };

    const saveSalaryRows = async () => {
        setSalarySaving(true);
        setSalaryError("");
        try {
            const lessonIds = salaryRows
                .filter((row) => row.is_resolved)
                .map((row) => Number(row.lesson_id))
                .filter((id) => Number.isFinite(id) && id > 0);

            if (!lessonIds.length) {
                setSalaryError("No resolved rows selected.");
                setSalarySaving(false);
                return;
            }

            await api.post("admin/income-expenses/salary/commit", {
                from: salaryFrom,
                to: salaryTo,
                lesson_ids: lessonIds,
            });

            setShowSalaryModal(false);
            setSalaryRows([]);
            setSalaryMeta({ total_rows: 0, resolved_rows: 0, total_amount: 0 });
            fetchRows(filters, pagination.page);
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                (typeof err.response?.data === "string" ? err.response.data : null) ||
                "Failed to save salary entries.";
            setSalaryError(msg);
        } finally {
            setSalarySaving(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <Typography component="h2" variant="h6" sx={{ m: 0 }}>
                    Finance
                </Typography>
                <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{
                        alignItems: "center",
                        flex: 1,
                        justifyContent: "flex-end",
                        minWidth: 0,
                    }}
                >
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowIncomeModal(true)}>Add Income</Button>
                    <Button variant="contained" color="error" startIcon={<AddIcon />} onClick={() => setShowExpenseModal(true)}>Add Expense</Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            const { from, to } = getLastMonthDateRangeISO();
                            setSalaryFrom(from);
                            setSalaryTo(to);
                            setSalaryRows([]);
                            setSalaryError("");
                            setShowSalaryModal(true);
                        }}
                    >
                        Generate Salary
                    </Button>
                    <Button
                        variant="contained"
                        color="info"
                        startIcon={<AddIcon />}
                        sx={{ color: "#fff" }}
                        onClick={() => setShowGenerateFromTemplate(true)}
                    >
                        From template
                    </Button>
                    <Button variant="outlined" startIcon={<DescriptionOutlinedIcon />} onClick={() => setShowTemplatesManager(true)}>
                        Templates
                    </Button>
                    <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportToExcel}>
                        Export to Excel
                    </Button>
                </Stack>
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <SummaryCard label="Total Income" value={formatMoney(Number(summary.income_total || 0))} color="success.main" />
                <SummaryCard label="Total Expenses" value={formatMoney(Number(summary.expense_total || 0))} color="error.main" />
                <SummaryCard label="Net Difference" value={formatMoney(Number(summary.difference || 0))} color={Number(summary.difference || 0) < 0 ? "error.main" : "success.main"} />
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gap: 1.5,
                    mb: 2,
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(3, minmax(0, 1fr))",
                        md: "repeat(4, minmax(0, 1fr))",
                        lg: "repeat(6, minmax(0, 1fr))",
                    },
                }}
            >
                <FormControl size="small">
                    <InputLabel id="direction-label">Direction</InputLabel>
                    <Select labelId="direction-label" label="Direction" value={filters.direction} onChange={(e) => setFilters((p) => ({ ...p, direction: e.target.value }))}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="income">Income</MenuItem>
                        <MenuItem value="expense">Expense</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small">
                    <InputLabel id="source-label">Source</InputLabel>
                    <Select labelId="source-label" label="Source" value={filters.source} onChange={(e) => setFilters((p) => ({ ...p, source: e.target.value }))}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="manual">Manual</MenuItem>
                        <MenuItem value="purchase">Purchase</MenuItem>
                        <MenuItem value="salary">Salary</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small">
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select labelId="status-label" label="Status" value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="processed">Processed</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                        <MenuItem value="canceled">Canceled</MenuItem>
                    </Select>
                </FormControl>
                <OutlinedInput size="small" type="date" value={filters.from} onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))} />
                <OutlinedInput size="small" type="date" value={filters.to} onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))} />
                <Button variant="outlined" onClick={() => setFilters({ direction: "", source: "", status: "", from: "", to: "" })}>Reset Filters</Button>
            </Box>

            <Table
                loading={loading}
                rows={rows}
                columns={columns}
                selactable={false}
                draggable={false}
                renderActions={(row) => (
                    <Stack direction="row" spacing={0} justifyContent="flex-end">
                        <Tooltip title="Edit">
                            <IconButton size="small" aria-label="Edit" onClick={() => setEditingEntry(row)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton size="small" color="error" aria-label="Delete" onClick={() => onDelete(row)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )}
            />

            <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                rowsPerPage={pagination.perPage}
                rowsPerPageOptions={[]}
                onPageChange={(event, newPage) => fetchRows(filters, newPage + 1)}
            />

            <IncomeExpenseModal open={showIncomeModal} direction="income" editingEntry={null} onClose={(refetch) => {
                setShowIncomeModal(false);
                if (refetch) fetchRows(filters, pagination.page);
            }} />

            <IncomeExpenseModal open={showExpenseModal} direction="expense" editingEntry={null} onClose={(refetch) => {
                setShowExpenseModal(false);
                if (refetch) fetchRows(filters, pagination.page);
            }} />

            <IncomeExpenseModal
                open={Boolean(editingEntry)}
                editingEntry={editingEntry}
                onClose={(refetch) => {
                    setEditingEntry(null);
                    if (refetch) fetchRows(filters, pagination.page);
                }}
            />

            <ExpenseTemplatesManagerDialog open={showTemplatesManager} onClose={() => setShowTemplatesManager(false)} />

            <GenerateFromTemplateDialog
                open={showGenerateFromTemplate}
                onClose={() => setShowGenerateFromTemplate(false)}
                onCommitted={() => fetchRows(filters, pagination.page)}
            />

            <Dialog open={showSalaryModal} onClose={() => !salarySaving && setShowSalaryModal(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Generate Salary</DialogTitle>
                <DialogContent>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: 1, mb: 2 }}>
                        <OutlinedInput size="small" type="date" value={salaryFrom} onChange={(e) => setSalaryFrom(e.target.value)} />
                        <OutlinedInput size="small" type="date" value={salaryTo} onChange={(e) => setSalaryTo(e.target.value)} />
                        <Button variant="outlined" onClick={loadSalaryPreview} disabled={salaryLoading || !salaryFrom || !salaryTo}>
                            {salaryLoading ? "Loading..." : "Load Preview"}
                        </Button>
                    </Stack>
                    {salaryError ? <Alert severity="error" sx={{ mb: 1 }}>{salaryError}</Alert> : null}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Rows: {salaryMeta.total_rows} • Resolved: {salaryMeta.resolved_rows} • Total: {formatMoney(salaryMeta.total_amount)}
                    </Typography>
                    <Box sx={{ maxHeight: 420, overflow: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                        <MuiTable size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Lesson</TableCell>
                                    <TableCell>Teacher</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Duration</TableCell>
                                    <TableCell>Rate</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {salaryRows.map((row) => (
                                    <TableRow key={row.lesson_id}>
                                        <TableCell>#{row.lesson_id}</TableCell>
                                        <TableCell>{row.teacher_name || "—"}</TableCell>
                                        <TableCell>{row.lesson_type_name || "—"}</TableCell>
                                        <TableCell>{row.duration_minutes || 0} min</TableCell>
                                        <TableCell>{row.hourly_rate != null ? formatMoney(row.hourly_rate) : "—"}</TableCell>
                                        <TableCell>{row.amount != null ? formatMoney(row.amount) : "—"}</TableCell>
                                        <TableCell>
                                            {row.is_resolved ? (
                                                <Typography variant="caption" color="success.main">Ready</Typography>
                                            ) : (
                                                <Typography variant="caption" color="error.main">{row.error || "Missing config"}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button color="error" size="small" onClick={() => removeSalaryRow(row.lesson_id)}>
                                                Remove
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!salaryRows.length ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <Typography variant="body2" color="text.secondary">No rows loaded.</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                            </TableBody>
                        </MuiTable>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowSalaryModal(false)} disabled={salarySaving}>Cancel</Button>
                    <Button variant="contained" sx={{ color: "#fff" }} onClick={saveSalaryRows} disabled={salarySaving || !salaryRows.length}>
                        {salarySaving ? "Saving..." : "Save Salary Expenses"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

